import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { DocumentItemPartial } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaClickOutsideDirective } from 'src/app/shared/directives';
import { RaEditTooltipDirective } from '../../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../../services/_edit-data.service';
import { EditItemsService } from '../../../../services/edit-items.service';
import { EditService } from '../../../../services/edit-service';
import {
    ItemSpacing,
    SPACING_KEYS,
    SpacingSide,
    hasSpacing,
    itemSpacingOf,
    parseSpaceInput,
    stepItemSpace,
} from '../../../../../../item-spacing';
import { formatPt } from '../../../../../../text-style-options';
import { RaRailMenuDirective } from '../rail-menu.directive';

/**
 * Botão "Espaçamento" do rail + menu com os 4 lados (configs `spaceTop`, `spaceBottom`,
 * `spaceLeft`, `spaceRight` do item, em pt): espaço extra em volta do campo no PDF, somado
 * ao respiro padrão. Vale para qualquer tipo de campo, por isso mora no card-base.
 *
 * `shown()` traz o valor AO VIVO do − / + e da digitação (o card-base aplica como margem
 * do card); o save sai 400ms depois que o usuário para, ou na hora quando o menu fecha.
 */
@Component({
    selector: 'spacing-chip',
    exportAs: 'spacing',
    host: { '[class.buttonless]': 'buttonless()' },
    templateUrl: 'spacing-chip.component.html',
    styleUrl: 'spacing-chip.component.scss',
    imports: [
        NgTemplateOutlet,
        RaIconComponent,
        RaSquareIconComponent,
        RaClickOutsideDirective,
        RaEditTooltipDirective,
        RaRailMenuDirective,
        TranslatePipe,
    ],
})
export class SpacingChipComponent {
    public field = input<DocumentItemPartial | undefined>();
    /**
     * Sem o botão do rail: só o menu, que quem hospeda abre (`showMenu`). No rail da mídia o
     * chip mora dentro do wrapper do ⋯ e o menu abre ancorado nele.
     */
    public buttonless = input<boolean>(false);

    public showMenu = signal(false);

    private _editService = inject(EditService);
    private _editDataService = inject(EditDataService);
    private _editItemsService = inject(EditItemsService);
    private _translate = inject(TranslateService);

    public blocked = computed(
        () =>
            this._editService.loading() ||
            this._editDataService.currentSessionIsDisabled() ||
            this._editDataService.documentIsBlocked()
    );

    /** Salvo. */
    public saved = computed(() => itemSpacingOf(this.field()?.configs as Record<string, unknown>));

    // Ao vivo: `undefined` = vale o salvo.
    private _live = signal<ItemSpacing | undefined>(undefined);
    private _timer: ReturnType<typeof setTimeout> | null = null;

    public shown = computed(() => this._live() ?? this.saved());
    public custom = computed(() => hasSpacing(this.shown()));

    /** Texto em digitação por lado (ausente = mostra o valor formatado). */
    public drafts = signal<Partial<Record<SpacingSide, string>>>({});

    constructor() {
        // Fechou o menu (clique fora, rolagem, outro menu): o que estava ao vivo salva já.
        effect(() => {
            if (this.showMenu()) return;
            untracked(() => {
                this.drafts.set({});
                this._flush();
            });
        });

        inject(DestroyRef).onDestroy(() => this._flush());
    }

    public toggleMenu(): void {
        this.showMenu.update(open => !open);
    }

    public text(side: SpacingSide): string {
        return this.drafts()[side] ?? formatPt(this.shown()[side], this._translate.getCurrentLang());
    }

    public step(side: SpacingSide, direction: 1 | -1): void {
        this._clearDraft(side);
        this._setLive(side, stepItemSpace(this.shown()[side], direction));
    }

    public onInput(side: SpacingSide, event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this.drafts.update(d => ({ ...d, [side]: raw }));
        const value = parseSpaceInput(raw);
        if (value !== null) this._setLive(side, value);
    }

    public onKeydown(side: SpacingSide, event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            this.step(side, event.key === 'ArrowUp' ? 1 : -1);
        }
    }

    /** Enter ou saída do campo: salva já e mostra o número formatado. */
    public commit(side: SpacingSide): void {
        this._clearDraft(side);
        this._flush();
    }

    /** "Voltar ao padrão": apaga as 4 chaves na hora. */
    public reset(): void {
        this.drafts.set({});
        this._cancel();
        this._live.set(undefined);
        if (hasSpacing(this.saved())) this._save({ top: 0, bottom: 0, left: 0, right: 0 });
    }

    private _clearDraft(side: SpacingSide): void {
        this.drafts.update(d => {
            const { [side]: _, ...rest } = d;
            return rest;
        });
    }

    private _setLive(side: SpacingSide, value: number): void {
        this._live.set({ ...this.shown(), [side]: value });
        this._cancel();
        this._timer = setTimeout(() => this._flush(), 400);
    }

    private _cancel(): void {
        if (this._timer) clearTimeout(this._timer);
        this._timer = null;
    }

    /** Salva o ao vivo (se mudou) e solta: o card volta a ler o salvo, já otimista. */
    private _flush(): void {
        this._cancel();
        const live = this._live();
        if (!live) return;
        const saved = this.saved();
        if ((Object.keys(SPACING_KEYS) as SpacingSide[]).some(side => live[side] !== saved[side])) {
            this._save(live);
        }
        this._live.set(undefined);
    }

    /** Otimista; o serviço desfaz se o servidor recusar. 0 vira `null`, que apaga a chave na API. */
    private _save(spacing: ItemSpacing): void {
        const field = this.field();
        if (!field?._id) return;

        const patch: Record<string, number | null> = {};
        for (const side of Object.keys(SPACING_KEYS) as SpacingSide[]) {
            patch[SPACING_KEYS[side]] = spacing[side] > 0 ? spacing[side] : null;
        }

        this._editItemsService.updateItemConfig(field._id, {
            ...(field.configs ?? {}),
            ...patch,
        });
    }
}
